import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/app/store/auth'

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '',
    timeout: 30000,
    withCredentials: true, // Important for httpOnly cookies
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
})

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value: any) => void
    reject: (error: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })

    failedQueue = []
}

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add Authorization header if access token is available and MFA is not required
        const { accessToken, mfaRequired, isAuthenticated } = useAuthStore.getState()

        // Block protected API calls until token is ready
        // Allow public endpoints (app config, health checks, etc.)
        const isApiCall = config.url?.startsWith('/api/')
        const isPublicEndpoint = config.url === '/api/app/config' || config.url === '/health'
        if (isApiCall && !isPublicEndpoint && (!accessToken || mfaRequired || !isAuthenticated)) {
            // Silently cancel the request to avoid spurious 403s during auth setup
            // Consumers should guard queries with auth-ready checks
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return Promise.reject(new axios.Cancel('auth-not-ready'))
        }
        if (accessToken && !mfaRequired) {
            config.headers.Authorization = `Bearer ${accessToken}`
            // Only log in development mode to reduce console noise
            if (import.meta.env.DEV && config.url?.startsWith('/api/')) {
                console.log('🔐 Added Authorization header:', {
                    url: config.url,
                    hasToken: true
                })
            }
        } else {
            // Only warn for API calls that should have auth (not login/totp endpoints or public endpoints)
            if (config.url?.startsWith('/api/') && !config.url?.includes('/auth/') && !isPublicEndpoint) {
                console.warn('🔐 Missing Authorization header for API call:', config.url)
            }
        }

        // Add CSRF token if available
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = generateRequestId()

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor
api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response
    },
    async (error) => {
        // Handle cancel errors (from request interceptor) - just reject them
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        const originalRequest = error.config

        // Skip processing if there's no request config (cancel errors, etc.)
        if (!originalRequest) {
            return Promise.reject(error)
        }

        // Handle 401/403 auth failures (but skip for logout requests)
        const isAuthFailure = (error.response?.status === 401) ||
            (error.response?.status === 403 && typeof error.response?.data?.message === 'string' && error.response.data.message.toLowerCase().includes('authentication'))
        if (isAuthFailure && !originalRequest._retry && !originalRequest.url?.includes('/auth/logout')) {
            const url = originalRequest.url || ''
            const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/totp')
            if (isAuthEndpoint) {
                return Promise.reject(error)
            }
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return api(originalRequest)
                }).catch(err => {
                    return Promise.reject(err)
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                // Try to refresh using the backend's refresh endpoint
                const { refreshToken } = useAuthStore.getState()
                const refreshResponse = await api.post('/auth/refresh', { refreshToken })
                // Support both direct and wrapped success responses
                const refreshedToken = refreshResponse.data?.accessToken ?? refreshResponse.data?.data?.accessToken

                // If refresh was successful, process queued requests
                processQueue(null, refreshedToken)
                isRefreshing = false

                // Retry the original request
                originalRequest.headers.Authorization = `Bearer ${refreshedToken}`
                // Update store tokens keeping existing refresh token
                const { setTokens } = useAuthStore.getState()
                if (refreshedToken) {
                    setTokens(refreshedToken, refreshToken || '', 15 * 60)
                }
                return api(originalRequest)
            } catch (refreshError) {
                // Refresh failed, process queue with error
                processQueue(refreshError, null)
                isRefreshing = false

                // Clear tokens and logout user
                const { forceLogout } = useAuthStore.getState()
                forceLogout()

                return Promise.reject(refreshError)
            }
        }

        // Handle logout requests - always clear state even on 401
        if (originalRequest.url?.includes('/auth/logout')) {
            const { forceLogout } = useAuthStore.getState()
            forceLogout()
            // Don't redirect here, let the logout hook handle it
            return Promise.reject(error)
        }

        // Handle 419 CSRF token mismatch
        if (error.response?.status === 419) {
            const { forceLogout } = useAuthStore.getState()
            forceLogout()
            return Promise.reject(error)
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
            console.warn('Access forbidden:', error.response.data)
        }

        // Emit global error event for toast notifications (non-auth errors only)
        if (error.response && error.response.status >= 500) {
            const msg = error.response?.data?.message || error.response?.data?.error || 'An unexpected server error occurred'
            window.dispatchEvent(new CustomEvent('api-error', { detail: { status: error.response.status, message: msg, url: originalRequest?.url } }))
        } else if (!error.response && error.request) {
            window.dispatchEvent(new CustomEvent('api-error', { detail: { status: 0, message: 'Unable to reach the server. Please check your connection.', url: originalRequest?.url } }))
        }

        return Promise.reject(error)
    }
)

// Helper function to generate request ID
function generateRequestId(): string {
    return Math.random().toString(36).substr(2, 9)
}

// Helper function to handle API errors
export function handleApiError(error: any): string {
    if (error.response?.data?.message) {
        return error.response.data.message
    }

    if (error.response?.data?.detail) {
        return error.response.data.detail
    }

    if (error.message) {
        return error.message
    }

    return 'An unexpected error occurred'
}

// Helper function to check if error is network related
export function isNetworkError(error: any): boolean {
    return !error.response && error.request
}

// Helper function to check if error is timeout
export function isTimeoutError(error: any): boolean {
    return error.code === 'ECONNABORTED' || error.message?.includes('timeout')
}

export default api



